import { twMerge } from 'tailwind-merge';
import useTranslation from 'next-translate/useTranslation';
import SwiperCore, { Mousewheel, Navigation } from 'swiper';
import Swiper, { SwiperSlide } from '@/components/common/Swiper';
import { CaratLeft, CaratRight } from '@weee-fe/kunlunicon';
import { CroppedImage } from '@weee-fe/module';
import useClickActionT2 from '@/tracks/useClickActionT2';
import { getReferralIdFromCookie } from '@/utils/common/referral';
import { ClickType } from '@/constants/track';
import { useRouter } from 'next/router';
import { isSayWeeeApp } from '@/utils/common/device';
import BENEFITS_BG from '@/components/sutdent-benefits/images/benefits-bg.png';
import { useMemo } from 'react';

// 开启鼠标滚轮和导航支持
SwiperCore.use([Mousewheel, Navigation]);

// 定义卡片数据接口
interface BenefitCardItem {
  type: string;
  icon: string;
  action_url: string;
  content: {
    title: string;
    sub_title: string;
  };
}

const CardItem = ({
  item,
  handleClickCard
}: {
  item: BenefitCardItem;
  handleClickCard: (e: any, item: BenefitCardItem) => void;
}) => {
  return (
    <div
      onClick={e => handleClickCard(e, item)}
      className="w-full min-w-[154px] h-[114px] box-border rounded-2xl p-3 flex flex-col justify-between border border-solid border-tint-black-25 enki-elevation-4 bg-white cursor-pointer">
      <div className="h-6">
        <CroppedImage
          url={item?.icon}
          alt="coupon icon"
          width={24}
          height={24}
          configs={{ renderWidth: 24, renderHeight: 24 }}
          wrapperClassName="w-6 h-6"
          className="w-full h-full object-cover"
        />
      </div>
      <div className="mt-3 flex flex-col justify-between h-full">
        {item?.content?.title && (
          <div
            className={twMerge('enki-body-xs text-primary-1 line-clamp-2', !item?.content?.sub_title && 'line-clamp-3')}
            dangerouslySetInnerHTML={{ __html: item?.content?.title }}></div>
        )}
        {item?.content?.sub_title && (
          <div
            className="enki-body-3xs-strong text-promo-200-fg-minor"
            dangerouslySetInnerHTML={{ __html: item?.content?.sub_title }}></div>
        )}
      </div>
    </div>
  );
};

const BenefitCards = ({ className, isPC, data = {} }: { className?: string; isPC?: boolean; data?: any }) => {
  const { t } = useTranslation('student');
  const router = useRouter();
  const { navigation_bar, hero_section } = data;
  const { buttonClick } = useClickActionT2();

  const cards = hero_section?.benefits || [];
  // PC端且卡片数量大于4时才展示More（假设4个以内能展示完整，不需要滚动查看更多）
  const showMore = isPC && cards.length > 4;

  const handleClickCard = (e, item: BenefitCardItem) => {
    e.stopPropagation();
    e.preventDefault();
    const type = item.type ? item.type.toLowerCase() : '';
    new Promise(resolve => {
      buttonClick({
        co: {
          target_nm: `action_${type}`,
          click_type: ClickType.normal
        },
        ctx: {
          user_source: getReferralIdFromCookie() ? 'referred' : 'normal'
        }
      });
      resolve(true);
    }).then(() => {
      if (isSayWeeeApp() || isPC) {
        window.location.href = item.action_url;
      } else {
        router.push(item.action_url);
      }
    });
  };

  return (
    <div className={twMerge('pt-2 pb-6', !cards?.length && 'pb-0 h-[46px] overflow-hidden', className)}>
      <div className={twMerge('px-5 mb-[14px]', !cards?.length && 'mb-0')}>
        <h2 className={twMerge('enki-heading-xl text-primary-1')}>{navigation_bar?.content?.title}</h2>
        {navigation_bar?.content?.sub_title && (
          <p className="enki-body-2xs text-surface-100-fg-minor mt-1.5">{navigation_bar?.content?.sub_title}</p>
        )}
      </div>

      <div className="relative group">
        {cards.length < 3 ? (
          <div className="flex justify-center px-5 space-x-6">
            {cards?.map((item, index) => {
              return (
                <div key={`${item.id}-${index}`} className="w-[calc((100%-52px)/2)] max-w-[304px] flex-1">
                  <CardItem item={item} handleClickCard={e => handleClickCard(e, item)} />
                </div>
              );
            })}
          </div>
        ) : (
          <Swiper
            slidesPerView="auto"
            freeMode
            mousewheel
            navigation={{
              nextEl: '.benefit-cards-next',
              prevEl: '.benefit-cards-prev'
            }}
            spaceBetween={12}
            slidesOffsetAfter={20}
            slidesOffsetBefore={20}>
            {cards?.map((item, index) => {
              return (
                <SwiperSlide key={`${item.id}-${index}`} className="flex-none !w-[154px] !h-[114px] flex items-stretch">
                  <CardItem item={item} handleClickCard={e => handleClickCard(e, item)} />
                </SwiperSlide>
              );
            })}
          </Swiper>
        )}
        {showMore && (
          <>
            <div className="absolute left-0 top-0 bottom-0 w-[60px] flex items-center justify-start z-10 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              <div className="benefit-cards-prev pointer-events-auto ml-2 cursor-pointer w-10 h-10 rounded-full bg-white shadow-md flex items-center justify-center text-surface-100-fg-default hover:bg-surface-50 transition-all [&.swiper-button-disabled]:opacity-0 [&.swiper-button-disabled]:pointer-events-none">
                <CaratLeft className="text-xl" />
              </div>
            </div>
            <div className="absolute right-0 top-0 bottom-0 w-[120px] flex items-center justify-end z-10 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              <div className="benefit-cards-next pointer-events-auto mr-2 cursor-pointer w-10 h-10 rounded-full bg-white shadow-md flex items-center justify-center text-surface-100-fg-default hover:bg-surface-50 transition-all [&.swiper-button-disabled]:opacity-0 [&.swiper-button-disabled]:pointer-events-none">
                <CaratRight className="text-xl" />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default BenefitCards;
